/**
 * [Item 4] View Teams - the permanent record of a completed draft.
 *
 * Every team, its drafted players as draft-style tiles, grouped by position
 * (QB / RB / WR / TE / K / DEF) rather than draft order, because that is how
 * a commissioner reads a roster back. The commissioner also gets the CSV
 * export here, not just in the end-of-draft modal - a draft-only league
 * routes straight to this page from the dashboard, so a forgetful
 * commissioner can always come back for the file.
 *
 * Read-only by design: nothing on this page mutates a roster.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { authHeaders } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { PlayerAvatar } from "../components/PlayerHeadshot";
import PositionBadge from "../components/PositionBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlayerTile = ({ player, showCost, isDark }) => (
  <div
    className={`flex items-center gap-2 px-2 py-1.5 rounded-md border ${
      isDark ? "bg-[#161616] border-[#252525]" : "bg-white border-[#E0E0E0]"
    }`}
    data-testid={`view-teams-player-${player.player_id}`}
  >
    <PlayerAvatar playerId={player.player_id} position={player.position} size="xs" />
    <div className="min-w-0 flex-1">
      <p className={`font-body text-xs font-medium truncate ${isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]"}`}>
        {player.name}
      </p>
      <p className={`font-body text-[10px] truncate ${isDark ? "text-[rgba(255,255,255,0.45)]" : "text-[rgba(0,0,0,0.50)]"}`}>
        {player.nfl_team} · Bye {player.bye_week ?? "-"} · R{player.round}P{player.pick}
      </p>
    </div>
    {showCost && player.cost != null && (
      <span className="font-mono text-[11px] tabular-nums text-[#FF6B00] shrink-0">
        ${player.cost}
      </span>
    )}
  </div>
);

const TeamCard = ({ team, positionOrder, showCost, isDark }) => {
  const byPosition = positionOrder
    .map((pos) => [pos, team.players.filter((p) => p.position === pos)])
    .filter(([, list]) => list.length > 0);
  const other = team.players.filter((p) => !positionOrder.includes(p.position));

  return (
    <section
      className={`rounded-lg border p-4 ${isDark ? "bg-[#1C1C1C] border-[#252525]" : "bg-[#FAFAFA] border-[#E0E0E0]"}`}
      data-testid={`view-teams-team-${team.user_id}`}
    >
      <header className="mb-3">
        <h2 className={`font-display font-black text-base uppercase tracking-wide truncate ${isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]"}`}>
          {team.team_name}
        </h2>
        <p className={`font-body text-xs ${isDark ? "text-[rgba(255,255,255,0.45)]" : "text-[rgba(0,0,0,0.50)]"}`}>
          {team.manager} · {team.players.length} player{team.players.length === 1 ? "" : "s"}
        </p>
      </header>

      {team.players.length === 0 ? (
        <p className="font-body text-xs text-[rgba(255,255,255,0.35)]">No picks recorded.</p>
      ) : (
        <div className="space-y-3">
          {[...byPosition, ...(other.length ? [["OTHER", other]] : [])].map(([pos, list]) => (
            <div key={pos}>
              <div className="flex items-center gap-2 mb-1.5">
                <PositionBadge position={pos === "OTHER" ? null : pos} size="sm" />
                <span className="font-display font-black text-[10px] uppercase tracking-widest text-[#FF6B00]">
                  {pos}
                </span>
                <span className="font-mono text-[10px] tabular-nums text-[rgba(255,255,255,0.35)]">
                  {list.length}
                </span>
              </div>
              <div className="space-y-1.5">
                {list
                  .sort((a, b) => a.overall_pick - b.overall_pick)
                  .map((p) => (
                    <PlayerTile key={p.player_id} player={p} showCost={showCost} isDark={isDark} />
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default function ViewTeamsPage() {
  const { id: leagueId } = useParams();
  const nav = useNavigate();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/draft/${leagueId}/results`, { headers: authHeaders() })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError("Could not load the draft results."); });
    return () => { cancelled = true; };
  }, [leagueId]);

  // The CSV route is authenticated, so it cannot be a plain link - fetch it
  // as a blob and hand the browser the download.
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/draft/${leagueId}/results.csv`, {
        headers: authHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(data?.league?.name || "league").replace(/[^\w-]+/g, "_")}_draft_results.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export failed. Try again.");
    } finally {
      setExporting(false);
    }
  }, [leagueId, data?.league?.name]);

  const bg = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";

  if (error && !data) {
    return (
      <div className={`min-h-screen ${bg} ${text} flex items-center justify-center px-6`}>
        <p className="font-body text-sm" data-testid="view-teams-error">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`min-h-screen ${bg} ${text} flex items-center justify-center`}>
        <Loader2 className="animate-spin text-[#FF6B00]" size={28} data-testid="view-teams-loading" />
      </div>
    );
  }

  const { league, teams, position_order: positionOrder, is_commissioner, format } = data;

  return (
    <div className={`min-h-screen ${bg} ${text} px-4 sm:px-8 py-6`} data-testid="view-teams-page">
      <header className="max-w-7xl mx-auto mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => nav("/dashboard")}
            className="flex items-center gap-1.5 font-body text-xs text-[rgba(255,255,255,0.50)] hover:text-[#FF6B00] transition-colors mb-2"
            data-testid="view-teams-back-btn"
          >
            <ArrowLeft size={13} /> Dashboard
          </button>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight truncate">
            {league.name}
          </h1>
          <p className={`font-body text-sm mt-1 ${isDark ? "text-[rgba(255,255,255,0.50)]" : "text-[rgba(0,0,0,0.55)]"}`}>
            {league.draft_type} draft · {league.teams} teams · {league.scoring}
            {league.date ? ` · ${league.date.slice(0, 10)}` : ""}
          </p>
        </div>
        {is_commissioner && (
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#FF6B00] hover:brightness-110 disabled:opacity-60 text-[#0A0A0A] font-display font-black uppercase tracking-wider text-xs transition-all"
            data-testid="view-teams-export-btn"
          >
            {exporting ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
            {exporting ? "Exporting" : "Export League"}
          </button>
        )}
      </header>

      {error && (
        <p className="max-w-7xl mx-auto mb-4 font-body text-xs text-[#FF4444]" data-testid="view-teams-inline-error">
          {error}
        </p>
      )}

      <div className="max-w-7xl mx-auto grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {teams.map((team) => (
          <TeamCard
            key={team.user_id}
            team={team}
            positionOrder={positionOrder}
            showCost={format !== "snake"}
            isDark={isDark}
          />
        ))}
      </div>
    </div>
  );
}
